import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2 } from 'lucide-react';
import { api, PromptQualityAnalyticsDto } from '../../services/api';

const SUB_SCORE_LABELS: { key: keyof PromptQualityAnalyticsDto['subScoreAverages']; label: string }[] = [
  { key: 'clarity', label: 'Clarity' },
  { key: 'specificity', label: 'Specificity' },
  { key: 'outputFormat', label: 'Output Format' },
  { key: 'constraints', label: 'Constraints' },
  { key: 'tokenEfficiency', label: 'Token Efficiency' },
];

function formatScore(score: number | null): string {
  return score == null ? '—' : score.toFixed(1);
}

export function PromptQualityPanel() {
  const [data, setData] = useState<PromptQualityAnalyticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await api.getPromptQualityAnalytics());
    } catch (err) {
      console.error('Failed to load prompt quality analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load prompt quality analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Prompt Quality</CardTitle>
        <CardDescription>
          Quality scores across the library — averages exclude prompts that have not been analyzed
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>
              Error loading prompt quality analytics: {error}
              <button onClick={loadData} className="ml-4 underline hover:no-underline">
                Try again
              </button>
            </AlertDescription>
          </Alert>
        ) : !data ? (
          <Alert>
            <AlertDescription>No prompt quality data available</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-8">
            {/* Overall stats */}
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="p-3 rounded border">
                <p className="text-xs text-muted-foreground">Overall Average Score</p>
                <p className="text-2xl font-bold">
                  {data.overallAverageScore == null ? '—' : `${data.overallAverageScore.toFixed(1)}/10`}
                </p>
              </div>
              <div className="p-3 rounded border">
                <p className="text-xs text-muted-foreground">Scored Prompts</p>
                <p className="text-2xl font-bold">
                  {data.scoredPromptCount}
                  <span className="text-sm font-normal text-muted-foreground"> of {data.totalPromptCount}</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Score distribution histogram */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Score Distribution</h4>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.scoreDistribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="bucket" stroke="#64748b" fontSize={11} />
                      <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                      <Tooltip formatter={(value: number) => [`${value} prompts`, 'Count']} />
                      <Bar dataKey="count" fill="#3b82f6" name="Prompts" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Improvement trend */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">
                  Improvement Trend (avg score by month)
                </h4>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.improvementTrend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={10} angle={-45} textAnchor="end" height={50} />
                      <YAxis stroke="#64748b" fontSize={11} domain={[0, 10]} />
                      <Tooltip
                        formatter={(value: number) => [`${value.toFixed(1)}/10`, 'Avg Score']}
                      />
                      <Line
                        type="monotone"
                        dataKey="averageScore"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                        name="Avg Score"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Sub-score averages */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Average by Dimension</h4>
                <div className="space-y-2">
                  {SUB_SCORE_LABELS.map(({ key, label }) => {
                    const value = data.subScoreAverages[key];
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <span className="text-sm w-32 shrink-0">{label}</span>
                        <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded"
                            style={{ width: `${((value ?? 0) / 10) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-10 text-right">{formatScore(value)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top feedback categories */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Top Feedback Categories</h4>
                <div className="space-y-2">
                  {data.topFeedbackCategories.map((item) => (
                    <div key={item.category} className="flex items-center justify-between p-2 rounded border">
                      <span className="text-sm font-medium">{item.category}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.promptCount} {item.promptCount === 1 ? 'prompt' : 'prompts'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Average by category */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Average Score by Category</h4>
                <div className="space-y-2">
                  {data.averageScoreByCategory.map((group) => (
                    <div key={group.id} className="flex items-center justify-between p-2 rounded border">
                      <span className="text-sm font-medium">{group.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {group.averageScore == null
                          ? 'No scored prompts'
                          : `${group.averageScore.toFixed(1)}/10 · ${group.scoredPromptCount} scored`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Average by tag */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Average Score by Tag</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {data.averageScoreByTag.map((group) => (
                    <div key={group.id} className="flex items-center justify-between p-2 rounded border">
                      <span className="text-sm font-medium">{group.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {group.averageScore == null
                          ? 'No scored prompts'
                          : `${group.averageScore.toFixed(1)}/10 · ${group.scoredPromptCount} scored`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
